import { BuiltInAttribute } from "@orsane/schema/tasks";
import { useQueryClient } from "@tanstack/react-query";
import {
    ArrowUpDown,
    ChevronDown,
    CornerDownRight,
    Grid,
    KanbanSquare,
    LayoutPanelTop,
    SlidersHorizontal,
    SquareCheck,
} from "lucide-react";
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn, preparePreferenceTaskListAttributes } from "@/lib/utils";
import { useUpdateTaskListPreferencesMutation } from "@/mutations/task-list-mutations";
import { useTaskListPreferences } from "@/queries/list-queries";
import type { Attribute } from "@/types/attribute";
import type { TaskListPreferences } from "@/types/list";
import AttributeSelect from "../AttributeSelect";
import { trpc } from "../QueryProvider";
import { useTaskQueryParams } from "./TaskQueryParamsContext";

const groupingOptions = [
    { label: "No grouping", value: null },
    { label: "Status", value: BuiltInAttribute.taskStatus },
    { label: "Priority", value: BuiltInAttribute.priority },
    { label: "Created date", value: BuiltInAttribute.createdAt },
    { label: "Due date", value: BuiltInAttribute.dueDate },
    { label: "Assignee", value: BuiltInAttribute.assignee },
    { label: "Created by", value: BuiltInAttribute.createdBy },
    { label: "Lists", value: BuiltInAttribute.lists },
    { label: "Parent task", value: BuiltInAttribute.parentTask },
];

const sortingOptions = [
    { label: "Manual", value: BuiltInAttribute.manual },
    { label: "Title", value: BuiltInAttribute.title },
    { label: "Status", value: BuiltInAttribute.taskStatus },
    { label: "Priority", value: BuiltInAttribute.priority },
    { label: "Created date", value: BuiltInAttribute.createdAt },
    { label: "Due date", value: BuiltInAttribute.dueDate },
    { label: "Created by", value: BuiltInAttribute.createdBy },
    { label: "Parent task", value: BuiltInAttribute.parentTask },
];

const completedTaskOptions = [
    { label: "Show all", value: "all" },
    { label: "Past day", value: "past_day" },
    { label: "Past week", value: "past_week" },
    { label: "Past month", value: "past_month" },
    { label: "None", value: "none" },
];

function TaskListDisplayOption({
    label,
    children,
    indent,
    icon: Icon,
}: {
    label: string;
    children: React.ReactNode;
    icon: React.ComponentType<any>;
    indent?: boolean;
}) {
    return (
        <div className={cn("flex items-center gap-2", indent && "pl-6")}>
            {<Icon className="w-4 h-4" />}
            <div className=" text-zinc-500">{label}</div>
            <div className="ml-auto">{children}</div>
        </div>
    );
}

export default function DisplayOptionsPopover({ disabled }: { disabled?: boolean }) {
    const [tab, setTab] = useState<string[]>(["list"]);
    const currentQueryParams = useTaskQueryParams();
    const { type, taskListId, parentTaskId } = currentQueryParams;
    const { preferences } = useTaskListPreferences({
        taskListId,
        parentTaskId,
        type,
    });
    const queryClient = useQueryClient();
    const { mutateAsync: updateTaskListPreferences } = useUpdateTaskListPreferencesMutation();

    const grouping = groupingOptions.find((o) => o.value === preferences["taskList.grouping"]);
    if (!grouping) {
        throw new Error("Grouping not found. There should be default grouping defined at this point.");
    }
    const groupOrder = preferences["taskList.groupOrder"] || "asc";
    const sorting = sortingOptions.find((o) => o.value === preferences["taskList.sorting"]);
    if (!sorting) {
        throw new Error("Sorting not found. There should be default sorting defined at this point.");
    }
    const taskOrder = preferences["taskList.taskOrder"] || "asc";
    const completedTasksOption =
        completedTaskOptions.find((o) => o.value === preferences["taskList.completedTasks"]) || completedTaskOptions[0];

    const handleGroupingChange = async (value: string) => {
        const newPreferences: TaskListPreferences = {
            ...preferences,
            "taskList.grouping": value,
        };
        await updateTaskListPreferences({
            type,
            taskListId,
            parentTaskId,
            preferences: newPreferences,
        });

        // Refresh the data from the list when changing the view. Like this we get the latest properties like sortIndex for new rows etc. instead of the temp values
        await queryClient.invalidateQueries(trpc.task.list.queryOptions(currentQueryParams));
    };

    const handleGroupOrderChange = async (value: string) => {
        const newPreferences: TaskListPreferences = {
            ...preferences,
            "taskList.groupOrder": value,
        };
        await updateTaskListPreferences({
            type,
            taskListId,
            parentTaskId,
            preferences: newPreferences,
        });

        // Refresh the data from the list when changing the view. Like this we get the latest properties like sortIndex for new rows etc. instead of the temp values
        await queryClient.invalidateQueries(trpc.task.list.queryOptions(currentQueryParams));
    };

    const handleTaskOrderChange = async (value: string) => {
        const newPreferences: TaskListPreferences = {
            ...preferences,
            "taskList.taskOrder": value,
        };
        await updateTaskListPreferences({
            type,
            taskListId,
            parentTaskId,
            preferences: newPreferences,
        });

        // Refresh the data from the list when changing the view. Like this we get the latest properties like sortIndex for new rows etc. instead of the temp values
        await queryClient.invalidateQueries(trpc.task.list.queryOptions(currentQueryParams));
    };

    const handleSortingChange = async (value: string) => {
        const newPreferences: TaskListPreferences = {
            ...preferences,
            "taskList.sorting": value,
        };
        // Refresh the data from the list when changing the view. Like this we get the latest properties like sortIndex for new rows etc. instead of the temp values
        await queryClient.invalidateQueries(trpc.task.list.queryOptions(currentQueryParams));

        await updateTaskListPreferences({
            type,
            taskListId,
            parentTaskId,
            preferences: newPreferences,
        });
    };

    const handleCompletedTasksChange = async (value: string) => {
        const newPreferences: TaskListPreferences = {
            ...preferences,
            "taskList.completedTasks": value,
        };
        await updateTaskListPreferences({
            type,
            taskListId,
            parentTaskId,
            preferences: newPreferences,
        });

        // Refresh the data from the list when changing the view. Like this we get the latest properties like sortIndex for new rows etc. instead of the temp values
        await queryClient.invalidateQueries(trpc.task.list.queryOptions(currentQueryParams));
    };

    const handleAttributesChange = async (value: Attribute[]) => {
        const newPreferences: TaskListPreferences = {
            ...preferences,
            "taskList.attributes": value,
        };
        await updateTaskListPreferences({
            type,
            taskListId,
            parentTaskId,
            preferences: newPreferences,
        });

        // No need to refetch. We already have all the data and the row sortIndex shouldnt change anyway.
    };

    return (
        <Popover>
            <PopoverTrigger
                render={
                    <Button className="text-base" variant="ghost" disabled={disabled}>
                        <SlidersHorizontal />
                        Display options
                    </Button>
                }
            />
            <PopoverContent className="p-0 w-[340px] gap-0!" side="bottom" align="end">
                <div className="p-4 border-b">
                    <ToggleGroup
                        value={tab}
                        onValueChange={(v) => {
                            if (v) setTab(v);
                        }}
                        variant="outline"
                        className="flex w-full!"
                    >
                        <ToggleGroupItem
                            className="flex-1! flex flex-col items-center h-auto py-3 text-sm gap-1"
                            value="list"
                        >
                            <Grid />
                            <div>List</div>
                        </ToggleGroupItem>
                        <ToggleGroupItem
                            className="flex-1! flex flex-col items-center h-auto py-3 text-sm gap-1"
                            value="board"
                        >
                            <KanbanSquare />
                            <div>Board</div>
                        </ToggleGroupItem>
                    </ToggleGroup>
                </div>
                {tab.includes("list") && (
                    <>
                        <div className="py-3 px-4 border-b">
                            <TaskListDisplayOption label="Grouping" icon={LayoutPanelTop}>
                                <DropdownMenu>
                                    <DropdownMenuTrigger
                                        render={
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-base font-normal text-zinc-500"
                                            >
                                                {grouping.label}
                                                <ChevronDown className="w-3 h-3" />
                                            </Button>
                                        }
                                    ></DropdownMenuTrigger>
                                    <DropdownMenuContent className="w-56" align="end">
                                        <DropdownMenuRadioGroup
                                            value={grouping.value}
                                            onValueChange={handleGroupingChange}
                                        >
                                            {groupingOptions.map((o) => (
                                                <React.Fragment key={o.value}>
                                                    <DropdownMenuRadioItem value={o.value as string}>
                                                        {o.label}
                                                    </DropdownMenuRadioItem>
                                                    {o.value === null && <DropdownMenuSeparator />}
                                                </React.Fragment>
                                            ))}
                                        </DropdownMenuRadioGroup>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </TaskListDisplayOption>
                            {!!grouping.value && (
                                <TaskListDisplayOption label="Order" icon={CornerDownRight} indent={true}>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger
                                            render={
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-base font-normal text-orsane-muted"
                                                >
                                                    {groupOrder === "asc" ? "Ascending" : "Descending"}
                                                    <ChevronDown className="w-3 h-3" />
                                                </Button>
                                            }
                                        ></DropdownMenuTrigger>
                                        <DropdownMenuContent className="w-56" align="end">
                                            <DropdownMenuRadioGroup
                                                value={groupOrder}
                                                onValueChange={handleGroupOrderChange}
                                            >
                                                <DropdownMenuRadioItem value="asc">Ascending</DropdownMenuRadioItem>
                                                <DropdownMenuRadioItem value="desc">Descending</DropdownMenuRadioItem>
                                            </DropdownMenuRadioGroup>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </TaskListDisplayOption>
                            )}
                            <TaskListDisplayOption label="Task Sorting" icon={ArrowUpDown}>
                                <DropdownMenu>
                                    <DropdownMenuTrigger
                                        render={
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-base font-normal text-orsane-muted"
                                            >
                                                {sorting.label}
                                                <ChevronDown className="w-3 h-3" />
                                            </Button>
                                        }
                                    ></DropdownMenuTrigger>
                                    <DropdownMenuContent className="w-56" align="end">
                                        <DropdownMenuRadioGroup
                                            value={sorting.value}
                                            onValueChange={handleSortingChange}
                                        >
                                            {sortingOptions.map((o) => (
                                                <DropdownMenuRadioItem key={o.value} value={o.value as string}>
                                                    {o.label}
                                                </DropdownMenuRadioItem>
                                            ))}
                                        </DropdownMenuRadioGroup>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </TaskListDisplayOption>
                            {sorting.value !== BuiltInAttribute.manual && (
                                <TaskListDisplayOption label="Order" icon={CornerDownRight} indent={true}>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger
                                            render={
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-base font-normal text-orsane-muted"
                                                >
                                                    {taskOrder === "asc" ? "Ascending" : "Descending"}
                                                    <ChevronDown className="w-3 h-3" />
                                                </Button>
                                            }
                                        ></DropdownMenuTrigger>
                                        <DropdownMenuContent className="w-56" align="end">
                                            <DropdownMenuRadioGroup value={taskOrder} onValueChange={handleTaskOrderChange}>
                                                <DropdownMenuRadioItem value="asc">Ascending</DropdownMenuRadioItem>
                                                <DropdownMenuRadioItem value="desc">Descending</DropdownMenuRadioItem>
                                            </DropdownMenuRadioGroup>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </TaskListDisplayOption>
                            )}
                            <TaskListDisplayOption label="Completed tasks" icon={SquareCheck}>
                                <DropdownMenu>
                                    <DropdownMenuTrigger
                                        render={
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-base font-normal text-orsane-muted"
                                            >
                                                {completedTasksOption.label}
                                                <ChevronDown className="w-3 h-3" />
                                            </Button>
                                        }
                                    ></DropdownMenuTrigger>
                                    <DropdownMenuContent className="w-56" align="end">
                                        <DropdownMenuRadioGroup
                                            value={completedTasksOption.value}
                                            onValueChange={handleCompletedTasksChange}
                                        >
                                            {completedTaskOptions.map((o) => (
                                                <DropdownMenuRadioItem key={o.value} value={o.value as string}>
                                                    {o.label}
                                                </DropdownMenuRadioItem>
                                            ))}
                                        </DropdownMenuRadioGroup>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </TaskListDisplayOption>
                        </div>
                        <div className="p-4">
                            <AttributeSelect
                                taskListId={taskListId}
                                type={type}
                                value={preparePreferenceTaskListAttributes(preferences["taskList.attributes"])}
                                onChange={handleAttributesChange}
                            />
                        </div>
                    </>
                )}
                {tab.includes("board") && <div className="p-4 ">Coming soon</div>}
            </PopoverContent>
        </Popover>
    );
}
